"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { SiteHero, SectionHead, CheckList, CtaBand } from "@/components/marketing/site-ui";
import { useSiteContent } from "@/components/marketing/use-site-content";

/**
 * Safety and trust.
 *
 * This page exists for two readers: a business owner deciding whether to put their real
 * name and business on the network, and the app stores, which both ask where the safety
 * and data commitments are published. It links straight through to the policies rather
 * than paraphrasing them, so there is one authoritative version of each claim.
 */
/* The order matches site.safetyPage.docs. Link targets are routing, not copy, so they live
   here rather than being handed to every translator as a string to leave alone. */
const DOC_LINKS = ["/privacy", "/terms", "/cookies", "/account-deletion", "/child-safety", "/contact"];

export function SafetyClient() {
  const c = useSiteContent();
  const page = c.safetyPage;

  return (
    <>
      <SiteHero eyebrow={page.eyebrow} title={page.title} lede={page.lede} primaryLabel={c.cta.primary} />

      <section className="border-y border-neutral-200 bg-white py-16 md:py-20">
        <div className="gs-page">
          <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
            {page.pillars.map((pillar) => (
              <div key={pillar.title} className="min-w-0 border-t-2 border-neutral-900/85 pt-6">
                <h2 className="gs-title text-balance text-neutral-900">{pillar.title}</h2>
                <p className="mt-3 text-[15px] leading-relaxed text-neutral-600">{pillar.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="gs-page grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="min-w-0">
            <SectionHead eyebrow={page.dataEyebrow} title={page.dataTitle} body={page.dataBody} />
          </div>
          <div className="min-w-0">
            <CheckList items={page.dataItems} />
          </div>
        </div>
      </section>

      <section className="border-t border-neutral-200 py-14 md:py-16">
        <div className="gs-page">
          <div className="rounded-[1.75rem] bg-[#090f39] p-8 text-white md:p-12">
            <p className="gs-eyebrow text-white/50">{page.childEyebrow}</p>
            <h2 className="gs-headline mt-3 max-w-[22ch] text-balance text-white">{page.childTitle}</h2>
            <p className="mt-4 max-w-2xl text-[15.5px] leading-relaxed text-white/70">{page.childBody}</p>
            <Link
              href="/child-safety"
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#090f39] transition-transform hover:scale-[1.02]"
            >
              {page.childCta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-16 pt-4 md:pb-20">
        <div className="gs-page">
          <SectionHead title={page.docsHeading} />
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {page.docs.map((doc, index) => (
              <Link
                key={DOC_LINKS[index]}
                href={DOC_LINKS[index]}
                className="group flex min-w-0 items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-white px-5 py-4 transition-all hover:border-primary/40 hover:shadow-[0_12px_30px_-18px_rgba(11,99,224,0.5)]"
              >
                <span className="min-w-0">
                  <span className="block text-[15px] font-bold text-neutral-900">{doc.label}</span>
                  <span className="mt-1 block text-[13.5px] leading-snug text-neutral-500">{doc.body}</span>
                </span>
                <ArrowRight
                  className="mt-1 h-4 w-4 shrink-0 text-neutral-300 transition-all group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
